import { ArrowRight, CheckCircle2, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

export default function Integrations() {
  useSEO({
    title: 'Integrations | Connect GreenTick with Your HR Tech Stack',
    description: 'Seamlessly integrate GreenTick AI with your existing ATS, HRIS, and communication tools for a unified recruitment workflow.',
    canonicalPath: '/integrations',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8">
              <span>← Back to Home</span>
            </Link>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Seamless Integration
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                With Your Workflow
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Connect GreenTick with your existing HR systems and tools for a unified hiring experience. Our platform integrates seamlessly with your workflow.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Effortless Setup</h3>
                <p className="text-slate-400">
                  Seamless integration with existing HR systems was effortless. It has made our recruitment process more efficient and effective.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Flexible Configuration</h3>
                <p className="text-slate-400">
                  Easily modify templates as requirements evolve. Adapts to different roles and industries with comprehensive questioning for each position.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Consistent Evaluation</h3>
                <p className="text-slate-400">
                  Provides consistency in the evaluation process across different teams, ensuring fair and standardized assessments.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Role-Specific Adaptation</h3>
                <p className="text-slate-400">
                  Adapts to different roles and industries, ensuring comprehensive and relevant questioning for each position.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">Integration Features</h2>
              <div className="space-y-4">
                {[
                  'ATS Integration',
                  'HRIS System Compatibility',
                  'Calendar Synchronization',
                  'Email Automation',
                  'API Access',
                  'Custom Webhooks',
                  'Single Sign-On (SSO)',
                  'Data Export & Import'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://dashboard.greentick.org.in/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
